import {
  RELAY_TTL_DAYS,
  entryOf,
  type HeldRecord,
  type HiType,
  type NewRecordVersion,
  type NoticeCursor,
  type PracticeErrorCode,
  type PracticeQuery,
  type PracticeResponse,
  type PracticeResult,
  type PublicPractice,
  type RecordEntry,
  type RecordStore,
  type RecordWrite,
  type Role,
  type SeenEnvelopes,
  type SharedConsents,
  type VaultKey,
} from '@curentia/shared';
import type { LocalStore } from './store';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Why a practice request did not succeed, with a message fit to show. */
export class PracticeError extends Error {
  constructor(
    readonly code: PracticeErrorCode | 'timeout' | 'no_holder' | 'holder_changed',
    message: string,
    readonly record?: HeldRecord,
  ) {
    super(message);
    this.name = 'PracticeError';
  }
}

interface HeldIndex {
  seq: number;
  entries: RecordEntry[];
}

/**
 * Records kept on this device in encrypted IndexedDB items: a practice's
 * records when this device is its record holder (a solo doctor's phone or a
 * pharmacist's tablet), under held:<practice>, or a patient's own records
 * under mine. Every version is its own item and nothing is overwritten. The
 * index item is written after the record, so a crash leaves at most an
 * unindexed version, which the repeated request writes again. Backups include
 * them like every other item.
 */
export class VaultRecordStore implements RecordStore {
  private index: { seq: number; entries: Map<string, RecordEntry> } | null = null;

  constructor(
    private readonly db: LocalStore,
    private readonly key: VaultKey,
    /** held:<practice ID> for a practice's records, mine for a patient's own. */
    private readonly prefix: string,
  ) {}

  static forPractice(db: LocalStore, key: VaultKey, practiceId: string): VaultRecordStore {
    return new VaultRecordStore(db, key, `held:${practiceId}`);
  }

  private get indexItem(): string {
    return `${this.prefix}:index`;
  }

  private recordItem(id: string, version: number): string {
    return `${this.prefix}:${id}:${version}`;
  }

  private async load() {
    if (!this.index) {
      const stored = await this.db.get<HeldIndex>(this.key, this.indexItem);
      this.index = { seq: stored?.seq ?? 0, entries: new Map((stored?.entries ?? []).map((entry) => [entry.id, entry])) };
    }
    return this.index;
  }

  async get(id: string): Promise<HeldRecord | null> {
    const entry = (await this.load()).entries.get(id);
    return entry ? ((await this.db.get<HeldRecord>(this.key, this.recordItem(id, entry.version))) ?? null) : null;
  }

  async since(seq: number): Promise<RecordEntry[]> {
    return [...(await this.load()).entries.values()].filter((entry) => entry.seq > seq).sort((a, b) => a.seq - b.seq);
  }

  async ofPatient(patientId: string): Promise<RecordEntry[]> {
    return [...(await this.load()).entries.values()].filter((entry) => entry.patientId === patientId).sort((a, b) => a.seq - b.seq);
  }

  async save(input: NewRecordVersion): Promise<HeldRecord> {
    const index = await this.load();
    const record: HeldRecord = { ...input, seq: index.seq + 1 };
    await this.db.put(this.key, this.recordItem(record.id, record.version), record);
    const entries = new Map(index.entries).set(record.id, entryOf(record));
    await this.db.put(this.key, this.indexItem, { seq: record.seq, entries: [...entries.values()] } satisfies HeldIndex);
    this.index = { seq: record.seq, entries };
    return record;
  }
}

/** Envelope IDs this device has handled, so a captured envelope cannot be replayed. Pruned after the relay's 30 days. */
export class VaultSeen implements SeenEnvelopes {
  private ids: Record<string, number> | null = null;

  constructor(
    private readonly db: LocalStore,
    private readonly key: VaultKey,
  ) {}

  private async load(): Promise<Record<string, number>> {
    if (!this.ids) {
      const stored = (await this.db.get<Record<string, number>>(this.key, 'seen')) ?? {};
      const oldest = Date.now() - (RELAY_TTL_DAYS + 1) * DAY_MS;
      this.ids = Object.fromEntries(Object.entries(stored).filter(([, at]) => at >= oldest));
    }
    return this.ids;
  }

  async has(id: string): Promise<boolean> {
    return id in (await this.load());
  }

  async add(id: string, createdAt: string): Promise<void> {
    const ids = { ...(await this.load()), [id]: Date.parse(createdAt) };
    await this.db.put(this.key, 'seen', ids);
    this.ids = ids;
  }
}

/** How far this device, as a record holder, has sent dispensing notices for one practice. */
export class VaultNoticeCursor implements NoticeCursor {
  constructor(
    private readonly db: LocalStore,
    private readonly key: VaultKey,
    private readonly practiceId: string,
  ) {}

  private get item(): string {
    return `notified:${this.practiceId}`;
  }

  async get(): Promise<number> {
    return (await this.db.get<{ seq: number }>(this.key, this.item))?.seq ?? 0;
  }

  async set(seq: number): Promise<void> {
    await this.db.put(this.key, this.item, { seq });
  }
}

/** Consents this device has already sent records under, so each is fulfilled once. Pruned when they expire. */
export class VaultShared implements SharedConsents {
  private ids: Record<string, number> | null = null;

  constructor(
    private readonly db: LocalStore,
    private readonly key: VaultKey,
  ) {}

  private async load(): Promise<Record<string, number>> {
    if (!this.ids) {
      const stored = (await this.db.get<Record<string, number>>(this.key, 'shared')) ?? {};
      const now = Date.now();
      this.ids = Object.fromEntries(Object.entries(stored).filter(([, until]) => until > now));
    }
    return this.ids;
  }

  async has(consentId: string): Promise<boolean> {
    return consentId in (await this.load());
  }

  async add(consentId: string, expiresAt: string): Promise<void> {
    const ids = { ...(await this.load()), [consentId]: Date.parse(expiresAt) };
    await this.db.put(this.key, 'shared', ids);
    this.ids = ids;
  }
}

/**
 * What a member's device keeps of a practice whose records live on a hub:
 * the patient list and the records of patients opened here, encrypted, so
 * they still show offline. Leaving the practice deletes it.
 */
export class RecordCache {
  constructor(
    private readonly db: LocalStore,
    private readonly key: VaultKey,
    private readonly practiceId: string,
  ) {}

  static prefix(practiceId: string): string {
    return `cache:${practiceId}:`;
  }

  private get patientsItem(): string {
    return `${RecordCache.prefix(this.practiceId)}patients`;
  }

  private recordsItem(patientId: string): string {
    return `${RecordCache.prefix(this.practiceId)}patient:${patientId}`;
  }

  async patients(): Promise<{ cursor: number; records: HeldRecord[] }> {
    return (await this.db.get<{ cursor: number; records: HeldRecord[] }>(this.key, this.patientsItem)) ?? { cursor: 0, records: [] };
  }

  async mergePatients(records: HeldRecord[], cursor: number): Promise<HeldRecord[]> {
    const current = await this.patients();
    const byId = new Map(current.records.map((record) => [record.id, record]));
    for (const record of records) {
      const known = byId.get(record.id);
      if (!known || known.version < record.version) byId.set(record.id, record);
    }
    const merged = [...byId.values()];
    await this.db.put(this.key, this.patientsItem, { cursor: Math.max(cursor, current.cursor), records: merged });
    return merged;
  }

  async records(patientId: string): Promise<HeldRecord[]> {
    return (await this.db.get<HeldRecord[]>(this.key, this.recordsItem(patientId))) ?? [];
  }

  async setRecords(patientId: string, records: HeldRecord[]): Promise<void> {
    await this.db.put(this.key, this.recordsItem(patientId), records);
  }

  /** Takes in one record just written, wherever it belongs. */
  async merge(record: HeldRecord): Promise<void> {
    if (record.kind === 'patient') await this.mergePatients([record], 0);
    const records = (await this.records(record.patientId)).filter((r) => r.id !== record.id);
    await this.setRecords(record.patientId, [...records, record].sort((a, b) => a.seq - b.seq));
  }

  /** Deletes every cached item of a practice, for example after leaving it. */
  static async purge(db: LocalStore, practiceId: string): Promise<void> {
    const prefix = RecordCache.prefix(practiceId);
    for (const key of await db.keys()) if (key.startsWith(prefix)) await db.remove(key);
  }
}

/** How a device asks a practice's record holder: directly when it is the holder, through the relay otherwise. */
export interface PracticeChannel {
  ask(query: PracticeQuery): Promise<PracticeResponse>;
}

function result<T extends PracticeResult['op']>(response: PracticeResponse, op: T): Extract<PracticeResult, { op: T }> {
  if (!response.ok) throw new PracticeError(response.error.code, response.error.message, response.error.record);
  if (response.result.op !== op) throw new PracticeError('invalid_request', 'The record holder answered a different question');
  return response.result as Extract<PracticeResult, { op: T }>;
}

/**
 * One practice's records as the screens use them. On the record holder they
 * come straight from this device's store. On a member's device they come
 * from the hub and are cached for offline use.
 */
export class PracticeRecords {
  constructor(
    readonly practice: PublicPractice,
    readonly roles: Role[],
    private readonly channel: PracticeChannel,
    private readonly cache: RecordCache | null,
  ) {}

  /** True when this device holds the practice's records. */
  get local(): boolean {
    return this.cache === null;
  }

  async cachedPatients(): Promise<HeldRecord[]> {
    return this.cache ? (await this.cache.patients()).records : this.syncPatients();
  }

  /** The practice's patients, fetched from the record holder page by page. */
  async syncPatients(): Promise<HeldRecord[]> {
    let since = this.cache ? (await this.cache.patients()).cursor : 0;
    const found: HeldRecord[] = [];
    for (;;) {
      const page = result(await this.channel.ask({ op: 'sync', since }), 'sync');
      found.push(...page.records);
      since = page.next;
      if (!page.more) break;
    }
    return this.cache ? this.cache.mergePatients(found, since) : found;
  }

  async cachedRecords(patientId: string): Promise<HeldRecord[]> {
    return this.cache ? this.cache.records(patientId) : this.fetchRecords(patientId);
  }

  /** One patient's records this member may read, fetched page by page. */
  async fetchRecords(patientId: string): Promise<HeldRecord[]> {
    const records: HeldRecord[] = [];
    let after = 0;
    for (;;) {
      const page = result(await this.channel.ask({ op: 'records', patientId, after }), 'records');
      records.push(...page.records);
      after = page.next;
      if (!page.more) break;
    }
    await this.cache?.setRecords(patientId, records);
    return records;
  }

  async save(write: RecordWrite): Promise<HeldRecord> {
    const { record } = result(await this.channel.ask({ op: 'put', record: write }), 'put');
    await this.cache?.merge(record);
    return record;
  }

  /** One record, fresh from the record holder. */
  async get(id: string): Promise<HeldRecord> {
    return result(await this.channel.ask({ op: 'get', id }), 'get').record;
  }

  /** The practice's dispensing records across patients: its dispensing register. */
  async register(): Promise<HeldRecord[]> {
    const records: HeldRecord[] = [];
    let since = 0;
    for (;;) {
      const page = result(await this.channel.ask({ op: 'register', since }), 'register');
      records.push(...page.records);
      since = page.next;
      if (!page.more) break;
    }
    return records;
  }

  /** Documents of one type across the practice's patients, such as the prescriptions a pharmacy works from. */
  async documents(hiType: HiType): Promise<HeldRecord[]> {
    const records: HeldRecord[] = [];
    let since = 0;
    for (;;) {
      const page = result(await this.channel.ask({ op: 'documents', hiType, since }), 'documents');
      records.push(...page.records);
      since = page.next;
      if (!page.more) break;
    }
    return records;
  }
}
